import { uniqueId } from 'lodash'
import { ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef } from 'react'
import { ArtboardContext } from './context'
import { useRenderer } from './hooks/use-renderer'
import { useShader } from './hooks/use-shader'
import { ArtboardFilter, ArtboardRasterizedRef } from './types'

type Props = {
    className?: string
    desynchronized?: boolean
    duration: number
    filter?: ArtboardFilter
    filterIntensity?: number
    preserveDrawingBuffer?: boolean
    transparency: boolean
}

const ArtboardRasterized = (
    { className, desynchronized, duration, filter, filterIntensity = 1, preserveDrawingBuffer, transparency }: Props,
    ref: ForwardedRef<ArtboardRasterizedRef>
) => {
    const idRef = useRef<string>(uniqueId('artboard-rasterized-'))
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const { height, renderer, width } = useContext(ArtboardContext)
    const rasterized = useRenderer(canvasRef.current, undefined, transparency, undefined, {
        desynchronized,
        preserveDrawingBuffer,
    })
    const shader = useShader(rasterized, filter)

    useImperativeHandle(ref, () => ({
        renderer: rasterized,
    }))

    useEffect(() => {
        rasterized?.setNodes([
            {
                id: idRef.current,
                draw: (progress: number) => {
                    if (!rasterized || !shader || !renderer) return

                    renderer.gl.canvas.height = height
                    renderer.gl.canvas.width = width
                    renderer.render(progress)

                    const texture = rasterized.createTexture(renderer.gl.canvas)
                    const loops = Math.floor(duration / 1)

                    shader.setProgress((progress * loops) % 1)
                    shader.setIntensity(filterIntensity)
                    shader.drawImage({
                        height,
                        texture,
                        width,
                        x: 0,
                        y: 0,
                    })
                },
            },
        ])
    }, [rasterized, shader, renderer, filterIntensity])

    return <canvas className={className} height={height} ref={canvasRef} width={width} />
}

export default forwardRef(ArtboardRasterized)
